import React, { useState, useRef, useEffect } from 'react'
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet'
import L from 'leaflet'
import toast from 'react-hot-toast'
import DirectionalCone from './DirectionalCone'

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
})

// Map click handler component
const MapClickHandler = ({ onMapClick, enabled }) => {
  useMapEvents({
    click: (e) => {
      if (enabled) {
        onMapClick(e.latlng.lat, e.latlng.lng)
      }
    }
  })
  return null
}

// Map recenter component
const MapRecenter = ({ position }) => {
  const map = useMap()
  
  useEffect(() => {
    if (position && position.length === 2) {
      map.setView(position, map.getZoom())
    }
  }, [position, map])
  
  return null
}

// Haversine formula to calculate distance between two points in feet
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 20902231 // Earth's radius in feet
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLon = (lon2 - lon1) * Math.PI / 180
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon/2) * Math.sin(dLon/2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
  return R * c
}

// Reverse geocoding function using Nominatim API
const reverseGeocode = async (lat, lng) => {
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`
    const response = await fetch(url, {
      headers: {
        'User-Agent': 'CitySeeCityFix/1.0 (https://www.cityseecityfix.com)'
      }
    })
    
    const data = await response.json()
    
    if (data && data.address) {
      const addressData = {
        house_number: data.address.house_number || '',
        road: data.address.road || data.address.street || '',
        address2: '', // Nominatim doesn't provide address2, so it remains empty
        city: data.address.city || data.address.town || data.address.village || '',
        state: data.address.state || '',
        postcode: data.address.postcode || '',
        county: data.address.county || '',
        country_iso2: data.address.country_code?.toUpperCase() || 'US'
      }
      return addressData
    }
    return null
  } catch (error) {
    console.error('Reverse geocoding error:', error)
    return null
  }
}

const IsolatedLocationMap = ({
  initialLatitude,
  initialLongitude,
  onAddressUpdate,
  editable = false,
  height = 'h-64',
  className = '',
  showUnlockSlider = true,
  // Directional cone props
  showDirectionalCone = false,
  coneDirection = 0, // in degrees (0-360)
  coneLength = 100, // in meters
  coneWidth = 30,   // in degrees
  coneOpacity = 0.6
}) => {
  const [mapUnlocked, setMapUnlocked] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [currentPosition, setCurrentPosition] = useState([initialLatitude, initialLongitude])
  const [userHasSetPosition, setUserHasSetPosition] = useState(false)
  const [shouldRecenter, setShouldRecenter] = useState(false)
  const originalPosition = useRef([initialLatitude, initialLongitude])

  // Reset recenter flag after recentering
  useEffect(() => {
    if (shouldRecenter) {
      const timer = setTimeout(() => {
        setShouldRecenter(false)
      }, 100) // Small delay to ensure recentering happens
      return () => clearTimeout(timer)
    }
  }, [shouldRecenter])

  // Handle map click to update location
  const handleMapClick = async (lat, lng) => {
    if (!mapUnlocked || isUpdating) return

    setIsUpdating(true)
    
    try {
      // Update internal position state immediately
      setCurrentPosition([lat, lng])
      setUserHasSetPosition(true)
      setShouldRecenter(true) // Flag to recenter the map
      
      // Calculate distance from original location
      let distanceMessage = ''
      if (originalPosition.current) {
        const distance = calculateDistance(
          originalPosition.current[0], 
          originalPosition.current[1], 
          lat, 
          lng
        )
        distanceMessage = `Moved ${Math.round(distance)} feet from original location`
      }

      // If moved more than 100 feet, do reverse geocoding
      if (originalPosition.current) {
        const distance = calculateDistance(
          originalPosition.current[0], 
          originalPosition.current[1], 
          lat, 
          lng
        )
        
        if (distance > 100) {
          const addressData = await reverseGeocode(lat, lng)
          if (addressData && onAddressUpdate) {
            onAddressUpdate(addressData, lat, lng)
          }
        }
      }

      // Show success message with distance
      if (distanceMessage) {
        toast.success(distanceMessage, { duration: 5000 })
      } else {
        toast.success('Location updated successfully', { duration: 3000 })
      }

    } catch (error) {
      console.error('Error updating location:', error)
      toast.error('Failed to update location')
    } finally {
      setIsUpdating(false)
    }
  }

  // Don't render if no coordinates
  if (!currentPosition || currentPosition.length !== 2) {
    return (
      <div className={`${height} ${className} flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600`}>
        <div className="text-center text-gray-500 dark:text-gray-400">
          <p>No location data available</p>
        </div>
      </div>
    )
  }

  const [lat, lng] = currentPosition

  // Validate coordinates
  if (isNaN(lat) || isNaN(lng)) {
    return (
      <div className={`${height} ${className} flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600`}>
        <div className="text-center text-gray-500 dark:text-gray-400">
          <p>Invalid location coordinates</p>
        </div>
      </div>
    )
  }

  return (
    <div className={`${className}`}>
      {/* Map Unlock Slider */}
      {editable && showUnlockSlider && (
        <div className="mb-4">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={mapUnlocked}
              onChange={(e) => {
                setMapUnlocked(e.target.checked)
                if (!e.target.checked) {
                  // Reset to original position when locking
                  setUserHasSetPosition(false)
                  setCurrentPosition(originalPosition.current)
                  setShouldRecenter(true) // Recenter to original position
                }
              }}
              className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 dark:focus:ring-primary-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Unlock map to drop a pin and update location
            </span>
          </label>
          {userHasSetPosition && (
            <div className="mt-2">
              <button
                onClick={() => {
                  setUserHasSetPosition(false)
                  setCurrentPosition(originalPosition.current)
                  setShouldRecenter(true) // Recenter to original position
                }}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
              >
                Reset to original location
              </button>
            </div>
          )}
        </div>
      )}

      {/* Map Container */}
      <div className={`${height} relative`} style={{ minHeight: '200px' }}>
        <MapContainer
          center={[lat, lng]}
          zoom={15}
          style={{ height: '100%', width: '100%', minHeight: '200px' }}
          className="rounded-lg border border-gray-300 dark:border-gray-600"
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          <Marker position={[lat, lng]} />
          {showDirectionalCone && (
            <DirectionalCone
              position={[lat, lng]}
              direction={coneDirection}
              coneLength={coneLength}
              coneWidth={coneWidth}
              opacity={coneOpacity}
            />
          )}
          {editable && (
            <MapClickHandler 
              onMapClick={handleMapClick} 
              enabled={mapUnlocked && !isUpdating}
            />
          )}
          {shouldRecenter && (
            <MapRecenter position={[lat, lng]} />
          )}
        </MapContainer>

        {/* Loading overlay */}
        {isUpdating && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
            <div className="bg-white dark:bg-gray-800 px-4 py-2 rounded-lg shadow-lg">
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                <span className="text-sm text-gray-700 dark:text-gray-300">Updating location...</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Coordinates Display */}
      <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
        <div>Lat: {lat.toFixed(6)} | Lng: {lng.toFixed(6)}</div>
        {userHasSetPosition && (
          <div className="text-green-600 dark:text-green-400 mt-0.5">
            ✓ Position manually updated
          </div>
        )}
      </div>
    </div>
  )
}

export default IsolatedLocationMap
